/**
 * Landing Page Component
 * Premium gold-themed landing page with integrated authentication
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import AuthModal from '../components/auth/AuthModal';
import SEO from '../components/SEO';
import { trackEvent } from '../utils/analytics';
import './LandingPage.css';

function LandingPage() {
  const { login, register, forgotPassword, isAuthenticated, loading, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Auth form state
  const [activeAuthTab, setActiveAuthTab] = useState('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerHeight, setRegisterHeight] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [formError, setFormError] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // Auth modal state
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Refs for animations
  const snowContainerRef = useRef(null);
  const headerRef = useRef(null);

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app');
    }
  }, [isAuthenticated, navigate]);

  // Auto-open the auth modal when arriving with ?signup=1 / ?signin=1 (e.g., from /sample)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === '1') {
      setActiveAuthTab('register');
      setAuthModalOpen(true);
    } else if (params.get('signin') === '1') {
      setActiveAuthTab('login');
      setAuthModalOpen(true);
    }
  }, []);

  // Initialize snow particles
  useEffect(() => {
    const container = snowContainerRef.current;
    if (!container) return;

    const particleCount = 40;
    for (let i = 0; i < particleCount; i++) {
      const snowflake = document.createElement('div');
      snowflake.className = 'snowflake';
      const size = Math.random() * 4 + 2;
      const left = Math.random() * 100;
      const delay = Math.random() * 15;
      const duration = Math.random() * 10 + 10;
      snowflake.style.cssText = `
        width: ${size}px;
        height: ${size}px;
        left: ${left}%;
        animation-delay: ${delay}s;
        animation-duration: ${duration}s;
        opacity: ${Math.random() * 0.5 + 0.3};
      `;
      container.appendChild(snowflake);
    }

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        if (window.pageYOffset > 100) {
          headerRef.current.classList.add('scrolled');
        } else {
          headerRef.current.classList.remove('scrolled');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure landing page uses gold theme (remove any other theme)
  useEffect(() => {
    const savedTheme = document.body.getAttribute('data-theme');
    // Force gold theme for landing page
    document.body.setAttribute('data-theme', 'gold');

    // Restore saved theme when leaving landing page
    return () => {
      const themeToRestore = localStorage.getItem('theme') || savedTheme || 'gold';
      document.body.setAttribute('data-theme', themeToRestore);
    };
  }, []);

  // Scroll reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll(
      '.landing-page .excellence-item, .landing-page .capability, .landing-page .journey-step, .landing-page .membership-card, .landing-page .section-header'
    );
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Smooth scroll handler
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const target = document.querySelector(sectionId);
    if (target) {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const targetPos = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({ top: targetPos, behavior: 'smooth' });
    }
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!loginEmail || !loginPassword) {
      setFormError('Please fill in all fields');
      return;
    }

    const result = await login(loginEmail, loginPassword, rememberMe);
    if (result.success) {
      trackEvent('login', { method: 'password' });
      navigate('/app');
    } else {
      setFormError(result.error || 'Login failed');
    }
  };

  // Register handler
  const handleRegister = async (e) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!registerName || !registerEmail || !registerPassword || !registerHeight) {
      setFormError('Please fill in all required fields');
      return;
    }
    // Validate height (required for accurate jump-height calibration)
    const heightNum = parseFloat(registerHeight);
    if (isNaN(heightNum) || heightNum < 100 || heightNum > 220) {
      setFormError('Please enter a valid height between 100-220 cm');
      return;
    }
    if (!agreeTerms) {
      setFormError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    const result = await register(registerEmail, registerPassword, registerName, heightNum);
    if (result.success) {
      trackEvent('sign_up', { method: 'password' });
      navigate('/app');
    } else {
      setFormError(result.error || 'Registration failed');
    }
  };

  // Forgot password handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setFormError('');
    setForgotSuccess(false);
    clearError();

    if (!forgotEmail) {
      setFormError('Please enter your email address');
      return;
    }

    setForgotLoading(true);
    try {
      const result = await forgotPassword(forgotEmail);
      if (result.success) {
        setForgotSuccess(true);
      } else {
        setFormError(result.error || 'Failed to send reset email');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  // Open auth modal with specific tab
  const openAuthModal = (tab = 'login') => {
    setActiveAuthTab(tab);
    setFormError('');
    clearError();
    setAuthModalOpen(true);
  };

  // Render the auth card (tabs + forms) - used in both modal and inline section
  const renderAuthCard = () => (
    <div className="auth-card">
      {/* Auth Tabs */}
      <div className="auth-tabs">
        <button
          className={`auth-tab ${activeAuthTab === 'login' ? 'active' : ''}`}
          onClick={() => { setActiveAuthTab('login'); setFormError(''); clearError(); }}
        >
          Sign In
        </button>
        <button
          className={`auth-tab ${activeAuthTab === 'register' ? 'active' : ''}`}
          onClick={() => { setActiveAuthTab('register'); setFormError(''); clearError(); }}
        >
          Create Account
        </button>
      </div>

      {/* Login Form */}
      {activeAuthTab === 'login' && (
        <form className="auth-form" onSubmit={handleLogin}>
          {(formError || error) && (
            <div className="auth-error-banner">
              {formError || error}
            </div>
          )}
          <div className="form-fields-single">
            <div className="form-field">
              <label>Email Address</label>
              <input
                type="email"
                placeholder="Enter your email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div className="form-field">
              <label>Password</label>
              <input
                type="password"
                placeholder="Enter your password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                disabled={loading}
                required
              />
            </div>
          </div>
          <div className="form-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember me
            </label>
            <button
              type="button"
              className="forgot-link"
              onClick={() => { setActiveAuthTab('forgot'); setFormError(''); setForgotSuccess(false); clearError(); }}
            >
              Forgot password?
            </button>
          </div>
          <button type="submit" className="btn-submit-gold" disabled={loading}>
            <span>{loading ? 'Signing In...' : 'Sign In'}</span>
            {!loading && <i className="fas fa-arrow-right"></i>}
          </button>
          <p className="form-disclaimer">
            <i className="fas fa-shield-alt"></i>
            Your information is protected with enterprise-grade security
          </p>
        </form>
      )}

      {/* Register Form */}
      {activeAuthTab === 'register' && (
        <form className="auth-form" onSubmit={handleRegister}>
          {(formError || error) && (
            <div className="auth-error-banner">
              {formError || error}
            </div>
          )}
          <div className="form-grid">
            <div className="form-field">
              <label>Full Name <span className="required-mark">*</span></label>
              <input
                type="text"
                placeholder="Enter your name"
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div className="form-field">
              <label>Email Address <span className="required-mark">*</span></label>
              <input
                type="email"
                placeholder="Enter your email"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div className="form-field">
              <label>Password <span className="required-mark">*</span></label>
              <input
                type="password"
                placeholder="Create a password"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                disabled={loading}
                required
              />
              <div className="password-checklist">
                <span className={registerPassword.length >= 8 ? 'check-met' : 'check-unmet'}>
                  {registerPassword.length >= 8 ? '\u2713' : '\u2717'} 8+ characters
                </span>
                <span className={/[A-Z]/.test(registerPassword) ? 'check-met' : 'check-unmet'}>
                  {/[A-Z]/.test(registerPassword) ? '\u2713' : '\u2717'} Uppercase
                </span>
                <span className={/[a-z]/.test(registerPassword) ? 'check-met' : 'check-unmet'}>
                  {/[a-z]/.test(registerPassword) ? '\u2713' : '\u2717'} Lowercase
                </span>
                <span className={/[0-9]/.test(registerPassword) ? 'check-met' : 'check-unmet'}>
                  {/[0-9]/.test(registerPassword) ? '\u2713' : '\u2717'} Number
                </span>
              </div>
            </div>
            <div className="form-field">
              <label>Height (cm) <span className="required-mark">*</span></label>
              <input
                type="number"
                placeholder="e.g., 165"
                value={registerHeight}
                onChange={(e) => setRegisterHeight(e.target.value)}
                disabled={loading}
                min="100"
                max="220"
                step="0.1"
                required
              />
              <small className="field-hint">Used to calibrate your jump-height measurements</small>
            </div>
          </div>
          <label className="checkbox-label terms-checkbox">
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              required
            />
            <span className="terms-text">I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></span>
          </label>
          <button type="submit" className="btn-submit-gold" disabled={loading}>
            <span>{loading ? 'Creating Account...' : 'Create My Free Account'}</span>
            {!loading && <i className="fas fa-crown"></i>}
          </button>
          <p className="form-free-note">
            <i className="fas fa-check"></i> Free during beta &middot; No credit card required
          </p>
          <p className="form-disclaimer">
            <i className="fas fa-shield-alt"></i>
            Your information is protected with enterprise-grade security
          </p>
        </form>
      )}

      {/* Forgot Password Form */}
      {activeAuthTab === 'forgot' && (
        <form className="auth-form" onSubmit={handleForgotPassword}>
          {forgotSuccess ? (
            <div className="forgot-success">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Check Your Email</h3>
              <p>If an account exists with that email, we've sent password reset instructions.</p>
              <button
                type="button"
                className="btn-submit-gold"
                onClick={() => { setActiveAuthTab('login'); setForgotSuccess(false); setForgotEmail(''); }}
              >
                <span>Back to Sign In</span>
                <i className="fas fa-arrow-right"></i>
              </button>
            </div>
          ) : (
            <>
              <div className="forgot-header">
                <h3>Reset Your Password</h3>
                <p>Enter your email address and we'll send you instructions to reset your password.</p>
              </div>
              {(formError || error) && (
                <div className="auth-error-banner">
                  {formError || error}
                </div>
              )}
              <div className="form-fields-single">
                <div className="form-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    disabled={forgotLoading}
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn-submit-gold" disabled={forgotLoading}>
                <span>{forgotLoading ? 'Sending...' : 'Send Reset Link'}</span>
                {!forgotLoading && <i className="fas fa-paper-plane"></i>}
              </button>
              <button
                type="button"
                className="btn-back-link"
                onClick={() => { setActiveAuthTab('login'); setFormError(''); clearError(); }}
              >
                <i className="fas fa-arrow-left"></i>
                Back to Sign In
              </button>
            </>
          )}
        </form>
      )}
    </div>
  );

  return (
    <div className="landing-page">
      {/* Auth Modal */}
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)}>
        {renderAuthCard()}
      </AuthModal>

      {/* SEO Meta Tags */}
      <SEO
        title="LumiSkating - Olympic-Level Figure Skating AI Assistant"
        description="Transform your skating with AI video analysis. 33-point pose detection, ISU scoring insights, and personalized coaching feedback."
        url="https://www.lumiskating.com/"
      />

      {/* Background Effects */}
      <div className="background-effects">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="light-rays"></div>
        <div className="snow-particles" ref={snowContainerRef}></div>
      </div>

      {/* Navigation */}
      <header className="header" ref={headerRef}>
        <nav className="nav">
          <div className="nav-brand">
            <div className="brand-mark">
              <img src="/skate.png" alt="LumiSkating Logo" width="38" height="38" />
            </div>
            <div className="brand-text">
              <span className="brand-name">Lumi<span className="brand-skating">Skating</span></span>
              <span className="brand-tagline">Elite Training</span>
            </div>
          </div>
          <ul className="nav-menu">
            <li><a href="#excellence" onClick={(e) => scrollToSection(e, '#excellence')}>Excellence</a></li>
            <li><a href="#capabilities" onClick={(e) => scrollToSection(e, '#capabilities')}>Capabilities</a></li>
            <li><a href="#journey" onClick={(e) => scrollToSection(e, '#journey')}>Your Journey</a></li>
            <li><a href="#membership" onClick={(e) => scrollToSection(e, '#membership')}>Membership</a></li>
            <li><a href="/about">About</a></li>
          </ul>
          <div className="nav-cta">
            <button className="link-text" onClick={() => openAuthModal('login')}>Sign In</button>
            <button className="btn-gold" onClick={() => { trackEvent('cta_click', { location: 'nav' }); openAuthModal('register'); }}>Start Free</button>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-badge">
            <span className="badge-icon"><i className="fas fa-crown"></i></span>
            <span className="badge-text">Empowering Skaters, Supporting Coaches</span>
          </div>
          <h1 className="hero-headline">
            <span className="line line-1">Where Figure Skating</span>
            <span className="line line-2">Champions Are <em>Made</em></span>
            <span className="sr-only">AI Figure Skating Coach & Training Assistant</span>
          </h1>
          <p className="hero-lead">
            Olympic-level video analysis for every skater — from your first axel to your next triple.
            Upload a jump and get biomechanical feedback, ISU-aligned scoring, and personalized AI
            coaching in minutes. Built for skaters, their coaches, and the parents cheering them on.
          </p>
          <div className="hero-actions">
            <button className="btn-primary-gold" onClick={() => { trackEvent('cta_click', { location: 'hero' }); openAuthModal('register'); }}>
              <span>Start Free</span>
              <i className="fas fa-long-arrow-alt-right"></i>
            </button>
            <div className="hero-video-trigger" onClick={() => setVideoModalOpen(true)}>
              <div className="video-icon">
                <div className="play-triangle"></div>
              </div>
              <span>Watch the demo</span>
            </div>
          </div>
          <p className="hero-free-note">
            <i className="fas fa-check"></i> Free during beta
            <span className="free-note-sep">·</span>
            <i className="fas fa-check"></i> No credit card
            <span className="free-note-sep">·</span>
            <i className="fas fa-check"></i> Unlimited AI analysis
          </p>
          <a
            className="hero-sample-link"
            href="/sample"
            onClick={(e) => { e.preventDefault(); trackEvent('cta_click', { location: 'hero_sample' }); navigate('/sample'); }}
          >
            <i className="fas fa-play-circle"></i> See a real analysis — no sign-up needed
          </a>
          <div className="hero-accolades">
            <div className="accolade">
              <span className="accolade-value">Olympic</span>
              <span className="accolade-label">Level Analysis</span>
            </div>
            <div className="accolade-divider"></div>
            <div className="accolade">
              <span className="accolade-value">ISU</span>
              <span className="accolade-label">Standards Aligned</span>
            </div>
            <div className="accolade-divider"></div>
            <div className="accolade">
              <span className="accolade-value">AI</span>
              <span className="accolade-label">Powered Precision</span>
            </div>
          </div>
        </div>
        <div className="hero-artwork">
          <div className="artwork-frame">
            <div className="frame-glow"></div>
            <div className="visual-ring ring-1"></div>
            <div className="visual-ring ring-2"></div>
            <div className="visual-ring ring-3"></div>
            <div className="skater-scene">
              <img src="/skater-hero.png" alt="Figure skater performing triple axel with AI pose analysis" className="skater-hero-image" />
              <div className="sparkles">
                <span className="sparkle s1"></span>
                <span className="sparkle s2"></span>
                <span className="sparkle s3"></span>
                <span className="sparkle s4"></span>
                <span className="sparkle s5"></span>
              </div>
            </div>
          </div>
          <div className="floating-cards">
            <div className="stat-card card-1">
              <div className="stat-icon"><i className="fas fa-chart-line"></i></div>
              <div className="stat-info">
                <span className="stat-value">85%</span>
                <span className="stat-name">Accuracy</span>
              </div>
            </div>
            <div className="stat-card card-2">
              <div className="stat-icon"><i className="fas fa-database"></i></div>
              <div className="stat-info">
                <span className="stat-value">DATA</span>
                <span className="stat-name">Driven</span>
              </div>
            </div>
            <div className="stat-card card-3">
              <div className="stat-icon"><i className="fas fa-brain"></i></div>
              <div className="stat-info">
                <span className="stat-value">24/7</span>
                <span className="stat-name">AI Assistant</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Excellence Section */}
      <section className="excellence" id="excellence">
        <div className="section-container">
          <div className="section-header">
            <div className="section-ornament">
              <span className="ornament-line"></span>
              <span className="ornament-diamond"><i className="fas fa-diamond"></i></span>
              <span className="ornament-line"></span>
            </div>
            <h2 className="section-headline">The Art of Excellence</h2>
            <p className="section-subheadline">
              Where technology meets artistry, and data transforms into gold medal performances
            </p>
          </div>
          <div className="excellence-grid">
            <div className="excellence-item">
              <div className="excellence-visual">
                <div className="visual-bg"></div>
                <div className="visual-icon">
                  <i className="fas fa-eye"></i>
                </div>
              </div>
              <div className="excellence-content">
                <h3>Precision Vision</h3>
                <p>33-point pose detection captures every nuance of your movement with sub-millimeter precision, analyzing what the human eye cannot see.</p>
              </div>
            </div>
            <div className="excellence-item featured">
              <div className="excellence-visual">
                <div className="visual-bg"></div>
                <div className="visual-icon">
                  <i className="fas fa-gem"></i>
                </div>
              </div>
              <div className="excellence-content">
                <h3>Elite Intelligence</h3>
                <p>Powered by advanced AI that understands figure skating at the Olympic level, providing insights that transform good into extraordinary.</p>
              </div>
            </div>
            <div className="excellence-item">
              <div className="excellence-visual">
                <div className="visual-bg"></div>
                <div className="visual-icon">
                  <i className="fas fa-heart"></i>
                </div>
              </div>
              <div className="excellence-content">
                <h3>Holistic Mastery</h3>
                <p>Beyond technique—mental strength, competition preparation, and the psychological edge that separates champions from competitors.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="capabilities" id="capabilities">
        <div className="section-container">
          <div className="capabilities-showcase">
            <div className="showcase-content">
              <div className="section-ornament left">
                <span className="ornament-diamond"><i className="fas fa-diamond"></i></span>
              </div>
              <h2 className="showcase-title">Unparalleled Analysis</h2>
              <p className="showcase-description">
                Upload any video and witness the transformation. Our AI dissects every frame,
                measuring biomechanics against ISU gold standards, revealing the path from
                where you are to where you dream to be.
              </p>
              <div className="capability-list">
                <div className="capability">
                  <div className="capability-marker"></div>
                  <div className="capability-text">
                    <h4>Frame-by-Frame Biomechanics</h4>
                    <p>Jump height, rotation speed, body angles, and center of mass tracking</p>
                  </div>
                </div>
                <div className="capability">
                  <div className="capability-marker"></div>
                  <div className="capability-text">
                    <h4>ISU Scoring Alignment</h4>
                    <p>Compare your technique against official competition criteria</p>
                  </div>
                </div>
                <div className="capability">
                  <div className="capability-marker"></div>
                  <div className="capability-text">
                    <h4>Personalized Coaching</h4>
                    <p>AI-generated feedback tailored to your unique movement patterns</p>
                  </div>
                </div>
                <div className="capability">
                  <div className="capability-marker"></div>
                  <div className="capability-text">
                    <h4>Progress Visualization</h4>
                    <p>Watch your evolution through comparative analysis over time</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="showcase-visual">
              <div className="analysis-preview">
                <div className="preview-header">
                  <span className="preview-title">Live Analysis</span>
                  <span className="preview-status"><span className="status-dot"></span> Processing</span>
                </div>
                <div className="preview-body">
                  <div className="pose-visualization">
                    <svg viewBox="0 0 200 300" className="pose-svg">
                      <line className="pose-line" x1="100" y1="40" x2="100" y2="90"/>
                      <line className="pose-line" x1="100" y1="90" x2="60" y2="70"/>
                      <line className="pose-line" x1="100" y1="90" x2="140" y2="70"/>
                      <line className="pose-line" x1="60" y1="70" x2="30" y2="100"/>
                      <line className="pose-line" x1="140" y1="70" x2="170" y2="100"/>
                      <line className="pose-line" x1="100" y1="90" x2="100" y2="160"/>
                      <line className="pose-line" x1="100" y1="160" x2="70" y2="220"/>
                      <line className="pose-line" x1="100" y1="160" x2="130" y2="220"/>
                      <line className="pose-line" x1="70" y1="220" x2="50" y2="280"/>
                      <line className="pose-line" x1="130" y1="220" x2="150" y2="280"/>
                      <circle className="pose-joint head" cx="100" cy="30" r="12"/>
                      <circle className="pose-joint" cx="100" cy="90" r="6"/>
                      <circle className="pose-joint" cx="60" cy="70" r="5"/>
                      <circle className="pose-joint" cx="140" cy="70" r="5"/>
                      <circle className="pose-joint" cx="30" cy="100" r="4"/>
                      <circle className="pose-joint" cx="170" cy="100" r="4"/>
                      <circle className="pose-joint" cx="100" cy="160" r="6"/>
                      <circle className="pose-joint" cx="70" cy="220" r="5"/>
                      <circle className="pose-joint" cx="130" cy="220" r="5"/>
                      <circle className="pose-joint" cx="50" cy="280" r="4"/>
                      <circle className="pose-joint" cx="150" cy="280" r="4"/>
                    </svg>
                  </div>
                  <div className="analysis-data">
                    <div className="data-row">
                      <span className="data-label">Jump Height</span>
                      <div className="data-bar"><div className="bar-fill" style={{'--width': '78%'}}></div></div>
                      <span className="data-value">0.58m</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Rotation</span>
                      <div className="data-bar"><div className="bar-fill" style={{'--width': '88%'}}></div></div>
                      <span className="data-value">3.5 rev</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Air Position</span>
                      <div className="data-bar"><div className="bar-fill" style={{'--width': '92%'}}></div></div>
                      <span className="data-value">Excellent</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Landing</span>
                      <div className="data-bar"><div className="bar-fill" style={{'--width': '85%'}}></div></div>
                      <span className="data-value">Clean</span>
                    </div>
                  </div>
                </div>
                <div className="preview-footer">
                  <span className="footer-text">Triple Axel Analysis Complete</span>
                  <span className="footer-score">GOE: +4</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Product Preview / Social Proof */}
      <section className="product-preview" id="preview">
        <div className="section-container">
          <div className="section-header">
            <div className="section-ornament">
              <span className="ornament-line"></span>
              <span className="ornament-diamond"><i className="fas fa-diamond"></i></span>
              <span className="ornament-line"></span>
            </div>
            <h2 className="section-headline">See It In Action</h2>
            <p className="section-subheadline">
              Real analysis from inside the app — on desktop and mobile
            </p>
          </div>
          <div className="preview-shots">
            <figure className="preview-shot">
              <img src="/screenshots/desktop-video-analysis.jpg" alt="LumiSkating frame-by-frame video analysis with pose overlay and biomechanics" loading="lazy" />
              <figcaption>Frame-by-frame analysis with pose overlay &amp; biomechanics</figcaption>
            </figure>
            <figure className="preview-shot">
              <img src="/screenshots/mobile-coaching-chat.jpg" alt="LumiSkating AI coaching chat on a phone" loading="lazy" />
              <figcaption>AI coaching in your pocket</figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="journey" id="journey">
        <div className="section-container">
          <div className="section-header">
            <div className="section-ornament">
              <span className="ornament-line"></span>
              <span className="ornament-diamond"><i className="fas fa-diamond"></i></span>
              <span className="ornament-line"></span>
            </div>
            <h2 className="section-headline">Your Path to Greatness</h2>
          </div>
          <div className="journey-timeline">
            <div className="timeline-track"></div>
            <div className="journey-step">
              <div className="step-marker">
                <span className="marker-number">I</span>
              </div>
              <div className="step-card">
                <h3>Capture</h3>
                <p>Record your training sessions with any camera. Upload to Lumi and let the AI begin its analysis.</p>
                <div className="step-visual">
                  <i className="fas fa-video"></i>
                </div>
              </div>
            </div>
            <div className="journey-step">
              <div className="step-marker">
                <span className="marker-number">II</span>
              </div>
              <div className="step-card">
                <h3>Analyze</h3>
                <p>Advanced AI processes every frame, extracting biomechanical data and comparing to elite standards.</p>
                <div className="step-visual">
                  <i className="fas fa-microchip"></i>
                </div>
              </div>
            </div>
            <div className="journey-step">
              <div className="step-marker">
                <span className="marker-number">III</span>
              </div>
              <div className="step-card">
                <h3>Understand</h3>
                <p>Receive detailed coaching insights, visualizations, and specific recommendations for improvement.</p>
                <div className="step-visual">
                  <i className="fas fa-lightbulb"></i>
                </div>
              </div>
            </div>
            <div className="journey-step">
              <div className="step-marker">
                <span className="marker-number">IV</span>
              </div>
              <div className="step-card">
                <h3>Excel</h3>
                <p>Apply the insights, track your progress, and watch as your performances reach new heights.</p>
                <div className="step-visual">
                  <i className="fas fa-trophy"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Section */}
      <section className="membership" id="membership">
        <div className="section-container">
          <div className="section-header">
            <div className="section-ornament">
              <span className="ornament-line"></span>
              <span className="ornament-diamond"><i className="fas fa-diamond"></i></span>
              <span className="ornament-line"></span>
            </div>
            <h2 className="section-headline">Free While We're in Beta</h2>
            <p className="section-subheadline">
              Every feature unlocked — no credit card, no commitment. Paid plans arrive after beta.
            </p>
          </div>
          <div className="beta-access-card">
            <div className="beta-access-badge">
              <span className="beta-pulse-dot"></span> Live Beta
            </div>
            <div className="plan-price">
              <span className="price-currency">$</span>
              <span className="price-amount">0</span>
              <span className="price-period">during beta</span>
            </div>
            <ul className="plan-features">
              <li><i className="fas fa-check"></i> <strong>Unlimited</strong> video uploads &amp; AI analysis</li>
              <li><i className="fas fa-check"></i> 33-point pose detection &amp; ISU-aligned scoring</li>
              <li><i className="fas fa-check"></i> Frame-by-frame coaching feedback</li>
              <li><i className="fas fa-check"></i> Training planning &amp; progress tracking</li>
              <li><i className="fas fa-check"></i> Video comparison &amp; coach notes</li>
            </ul>
            <button
              className="btn-plan-gold"
              onClick={() => { trackEvent('cta_click', { location: 'membership' }); openAuthModal('register'); }}
            >
              Start Free — No Credit Card
            </button>
            <p className="beta-access-note">
              Join now as a founding member and help shape LumiSkating.
            </p>
          </div>
        </div>
      </section>

      {/* Auth Section */}
      <section className="auth-section" id="join">
        <div className="auth-bg">
          <div className="auth-gradient"></div>
        </div>
        <div className="section-container">
          <div className="auth-content">
            <div className="auth-header">
              <h2>Start Free — No Credit Card Required</h2>
              <p>Join skaters, coaches, and families transforming their training with AI. Free while we're in beta.</p>
            </div>

            {/* Auth Card - wraps tabs and form together */}
            {renderAuthCard()}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="brand-mark">
                <img src="/skate.png" alt="LumiSkating Logo" width="32" height="32" />
              </div>
              <div className="brand-text">
                <span className="brand-name">Lumi<span className="brand-skating">Skating</span></span>
              </div>
            </div>
            <p className="footer-tagline">The gold standard in AI-powered figure skating training</p>
          </div>
          <div className="footer-links">
            <div className="link-group">
              <h4>Product</h4>
              <a href="#">Features</a>
              <a href="#">Pricing</a>
              <a href="#">Updates</a>
            </div>
            <div className="link-group" hidden={true}>
              <h4>Resources</h4>
              <a href="#">Documentation</a>
              <a href="#">Tutorials</a>
              <a href="#">Blog</a>
            </div>
            <div className="link-group">
              <h4>Company</h4>
              <a href="/about">About</a>
              <a href="mailto:support@lumiskating.com">Contact</a>
            </div>
            <div className="link-group">
              <h4>Legal</h4>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <a href="#">Security</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025-2026 LumiSkating. All rights reserved.</p>
            <div className="social-icons">
              <a href="#"><i className="fab fa-instagram"></i></a>
              <a href="#"><i className="fab fa-youtube"></i></a>
              <a href="#"><i className="fab fa-twitter"></i></a>
            </div>
          </div>
        </div>
      </footer>

      {/* Video Demo Modal */}
      {videoModalOpen && (
        <div className="video-modal-overlay" onClick={() => setVideoModalOpen(false)}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setVideoModalOpen(false)}>
              <i className="fas fa-times"></i>
            </button>
            <div className="video-modal-content">
              <iframe
                src="https://www.youtube-nocookie.com/embed/4kd3_Lbsk8M?autoplay=1&modestbranding=1&rel=0&iv_load_policy=3&showinfo=0"
                title="LumiSkating Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LandingPage;
